import React, { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { auth } from '../api/authApi';
import { Loader } from '@mantine/core';
import './Auth.css';
import LogoSvg from '../assets/logo.svg';

interface NewPasswordFormProps {
  onSuccess: () => void;
}

export const NewPasswordForm: React.FC<NewPasswordFormProps> = ({ onSuccess }) => {
  const [searchParams] = useSearchParams();
  const rawToken = searchParams.get('token');
  
  // URL decode the token to handle special characters
  const token = rawToken ? decodeURIComponent(rawToken) : null;
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [rawToken, token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await auth.resetPassword(token, password);
      setSuccess(true);
    } catch (err: any) {
      setError('Invalid or expired reset link. Please request a new one.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-container">
        <header className="auth-header-bar">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={LogoSvg} alt="SymphonyTodo Logo" style={{ height: 40, width: 40, display: 'block', marginRight: 10 }} />
            <h1 style={{ fontWeight: 700, fontSize: 28, letterSpacing: '-1px', margin: 0, textAlign: 'center', lineHeight: '40px' }}>SymphonyTodo</h1>
          </div>
        </header>
        <main className="auth-content-section">
          <div className="auth-card">
            <div className="auth-header">
              <h1>Password Reset</h1>
              <p>Your password has been successfully reset</p>
            </div>
            <button 
              className="auth-submit-btn"
              onClick={onSuccess}
            >
              Sign In
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <header className="auth-header-bar">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={LogoSvg} alt="SymphonyTodo Logo" style={{ height: 40, width: 40, display: 'block', marginRight: 10 }} />
          <h1 style={{ fontWeight: 700, fontSize: 28, letterSpacing: '-1px', margin: 0, textAlign: 'center', lineHeight: '40px' }}>SymphonyTodo</h1>
        </div>
      </header>
      <main className="auth-content-section">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Create New Password</h1>
            <p>Enter your new password below</p>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            {error && <div className="error-message">{error}</div>}
            
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <div className="input-wrapper">
                <Lock size={18} className="input-icon" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  required
                  autoComplete="new-password"
                  autoCorrect="off"
                  spellCheck="false"
                  minLength={6}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="input-wrapper">
                <Lock size={18} className="input-icon" />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  autoComplete="new-password"
                  autoCorrect="off"
                  spellCheck="false"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button type="submit" className="auth-submit-btn" disabled={loading || !password || !confirmPassword}>
              {loading ? <Loader type="bars" size="sm" color="white" /> : 'Reset Password'}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};
